/**
 * Массовые действия карточки хозяйства (AK-746).
 *
 * Правило одной строкой (`frontend-vite/CLAUDE.md`, «Массовые действия»):
 * массовое действие = ОДНО действие, включая неуспех. Отсюда всё, что ниже:
 *
 *   · один запрос на пакет там, где ручка принимает список (архив, перенос в
 *     отделение), и последовательный цикл внутри ОДНОЙ операции там, где
 *     список ручка не принимает (восстановление);
 *   · отказ на одном поле не отменяет остальные и не прячет сделанное;
 *   · ОДИН итог словами — с ИМЕНАМИ полей и причинами, без идентификаторов;
 *   · обновление экрана — независимо от исхода (это делает вызывающий, в
 *     `onSettled`).
 *
 * Здесь — всё, что проверяется без DOM: сами запросы, счёт выборки и текст
 * итога.
 */
import { api, ApiError } from '@/lib/api';
import { pluralRu } from '@/lib/dateFmt';
import type { ToastKind } from '@/lib/toast';
import type { FarmFieldRow } from './types';

const HA = new Intl.NumberFormat('ru-RU', { maximumFractionDigits: 0 });
const FIELD_FORMS = ['поле', 'поля', 'полей'] as const;

/** Итог пакета — ровно один тост. */
export interface BulkOutcome {
  kind: ToastKind;
  message: string;
  /** Сколько полей реально изменилось — по нему вызывающий сбрасывает выбор. */
  doneCount: number;
}

/** Сводка по отмеченным строкам — то, что человек видит в диалоге. */
export interface SelectionStats {
  count: number;
  areaHa: number;
  /** Сколько из отмеченных уже разложены в плановом занятии. */
  planned: number;
}

export function selectionStats(
  rows: readonly FarmFieldRow[],
  selected: ReadonlySet<string>,
): SelectionStats {
  let areaHa = 0;
  let planned = 0;
  let count = 0;
  for (const row of rows) {
    if (!selected.has(row.id)) continue;
    count += 1;
    areaHa += row.area_ha ?? 0;
    if (row.plan_crop) planned += 1;
  }
  return { count, areaHa, planned };
}

/** Имена, а не идентификаторы: «не удалось на поле f-8f3a…» никому не поможет. */
function nameOf(names: ReadonlyMap<string, string>, id: string): string {
  return names.get(id) ?? 'без названия';
}

/**
 * Тон и текст итога — общая форма для всех трёх действий.
 *
 * Тон по исходу: всё получилось → success (самоскрывается); совсем ничего →
 * error; наполовину → warning. Warning и error висят до закрытия — список
 * отказов не должен исчезнуть через четыре секунды.
 */
export function bulkOutcome({
  doneIds,
  failed,
  names,
  areaById,
  doneLabel,
  emptyLabel,
}: {
  doneIds: readonly string[];
  failed: readonly { field_id: string; reason: string }[];
  names: ReadonlyMap<string, string>;
  areaById: ReadonlyMap<string, number>;
  /** «В архив» / «Перенесено» / «Восстановлено». */
  doneLabel: string;
  /** Что сказать, когда не получилось ничего. */
  emptyLabel: string;
}): BulkOutcome {
  const area = doneIds.reduce((sum, id) => sum + (areaById.get(id) ?? 0), 0);
  const okPart =
    doneIds.length > 0
      ? `${doneLabel}: ${doneIds.length} ${pluralRu(doneIds.length, FIELD_FORMS)} · ${HA.format(area)} га`
      : emptyLabel;
  const failPart =
    failed.length > 0
      ? ` Не удалось: ${failed
          .map((f) => `${nameOf(names, f.field_id)} (${f.reason})`)
          .join('; ')}.`
      : '';
  const kind: ToastKind =
    doneIds.length === 0 ? 'error' : failed.length > 0 ? 'warning' : 'success';
  return { kind, message: `${okPart}.${failPart}`, doneCount: doneIds.length };
}

function indexRows(rows: readonly FarmFieldRow[]) {
  return {
    names: new Map(rows.map((r) => [r.id, r.name])),
    areaById: new Map(rows.map((r) => [r.id, r.area_ha ?? 0])),
  };
}

/** Отказ всего пакета (нет прав, сезон заперт, сеть) — тоже ОДИН итог. */
function wholeBatchFailure(err: unknown, what: string): BulkOutcome {
  return {
    kind: 'error',
    message:
      err instanceof ApiError
        ? `Не удалось ${what}: ${err.detail}`
        : `Не удалось ${what} — попробуйте ещё раз`,
    doneCount: 0,
  };
}

/**
 * Пакетная архивация — ручка AK-750 (`POST /api/v1/fields/archive`).
 *
 * Свою ручку карточка не заводит: чистка списка полей — то же действие с той
 * же семантикой (архив поля уносит его занятия), просто вызванное с другого
 * экрана. Два эндпоинта на одно действие разъехались бы на первой же правке.
 * Отличие карточки — только в форме причины: код из справочника плюс
 * комментарий (`archiveReason.ts`), сама строка едет в то же поле.
 */
export async function archiveFields(
  ids: readonly string[],
  reason: string,
  rows: readonly FarmFieldRow[],
): Promise<BulkOutcome> {
  const { names, areaById } = indexRows(rows);
  try {
    const resp = await api.post<{
      archived_ids: string[];
      failed: { field_id: string; reason: string }[];
    }>('/api/v1/fields/archive', { field_ids: [...ids], reason });
    return bulkOutcome({
      doneIds: resp.archived_ids,
      failed: resp.failed,
      names,
      areaById,
      doneLabel: 'В архив',
      emptyLabel: 'Ни одно поле не заархивировано',
    });
  } catch (err) {
    return wholeBatchFailure(err, 'заархивировать поля');
  }
}

/** Пакетный перенос в отделение. `brigadeId = null` — «Без отделения». */
export async function moveFieldsToBrigade(
  farmId: string,
  ids: readonly string[],
  brigadeId: string | null,
  rows: readonly FarmFieldRow[],
): Promise<BulkOutcome> {
  const { names, areaById } = indexRows(rows);
  try {
    const resp = await api.post<{
      moved_ids: string[];
      failed: { field_id: string; reason: string }[];
    }>(`/api/v1/farms/${farmId}/fields/move-to-brigade`, {
      field_ids: [...ids],
      brigade_id: brigadeId,
    });
    return bulkOutcome({
      doneIds: resp.moved_ids,
      failed: resp.failed,
      names,
      areaById,
      doneLabel: 'Перенесено',
      emptyLabel: 'Ни одно поле не перенесено',
    });
  } catch (err) {
    return wholeBatchFailure(err, 'перенести поля');
  }
}

/**
 * Пакетное восстановление из архива — цикл `for … of await`, а не веер.
 *
 * Пакетной ручки восстановления на бэкенде НЕТ, и заводить её «под кнопку»
 * нельзя: восстановление тянет каскад посевов, и семантику пакета для него
 * никто не утверждал (тот же вывод в AK-750). Канон это прямо разрешает —
 * «либо бэк принимает список, либо цикл `for … of await` внутри ОДНОЙ
 * операции»: снаружи действие всё равно остаётся одним, с одним итогом.
 *
 * Именно последовательно, а не `Promise.all`: сорок одновременных запросов на
 * восстановление — это сорок запросов разом.
 */
export async function restoreFields(
  ids: readonly string[],
  rows: readonly FarmFieldRow[],
): Promise<BulkOutcome> {
  const { names, areaById } = indexRows(rows);
  const doneIds: string[] = [];
  const failed: { field_id: string; reason: string }[] = [];
  for (const id of ids) {
    try {
      await api.post(`/api/v1/fields/${id}/restore`, { restore_sowings: true });
      doneIds.push(id);
    } catch (err) {
      // Копим, а не бросаем: отказ на одном поле не отменяет остальные и не
      // прячет уже восстановленные.
      failed.push({
        field_id: id,
        reason: err instanceof ApiError ? err.detail : 'ошибка сети',
      });
    }
  }
  return bulkOutcome({
    doneIds,
    failed,
    names,
    areaById,
    doneLabel: 'Восстановлено',
    emptyLabel: 'Ни одно поле не восстановлено',
  });
}
